import { Injectable, inject } from '@angular/core';
import { DbService } from '../db/db.service';
import { StorageService } from '../storage/storage.service';
import { Course } from '../../models/course';
import { isNullOrUndefined } from '@pineapplelab/util';
import { Project } from '../../models/project';
import { LanguageCourse } from '../../models/languageCourse';
import { LanguageCourseService } from '../language-course/language-course.service';
import { Category } from '../../models/category';
import { cloneDeep } from 'lodash';
import { PublishStatusEnum } from '../../enums/publishStatus.enum';
import { UniqueIdService } from '../numeric-id/numeric-id.service';
import firebase from "firebase/compat/app";
import { DocumentReference } from "@angular/fire/compat/firestore";
import { RoleEnum } from '../../enums/role.enum';
import { MediaLibraryService } from '../media-library/media-library.service';
import { Chapter } from '../../models/chapter';
import { ApiService } from '../api/api.service';
import { Language } from '../../utils/language';
import { environment } from '../../../environments/environment';

@Injectable({
	providedIn: 'root'
})
export class CourseService {

	private db = inject(DbService);
	private storageService = inject(StorageService);
	private languageCourseService = inject(LanguageCourseService);
	private uniqueIdService = inject(UniqueIdService);
	private mediaLibraryService = inject(MediaLibraryService);
	private api = inject(ApiService);

	async save(model: Course, languageCourse: LanguageCourse[], projectId: string) {
		if (!isNullOrUndefined(model?.__fileImage)) {
			model.image = await this.storageService.saveOrRemoveImagesIfNecessary(model.__fileImage, `${Project.PATH}/${projectId}/${Course.PATH}/${model.id}/${model.__fileImage.name}`);
		}
		const json = model.toJson();

		await this.db.getProyIdRef(projectId).collection(Course.PATH).doc(model.id).set(json, { merge: true });

		for (const language of languageCourse) {
			if (isNullOrUndefined(language.id)) {
				language.duration = this.calculateDuration(language.chapters);
				language.id = this.db.generateFirebaseUniqueId();
			}
			await this.languageCourseService.save(language, projectId, model.id);
		}
		return json;
	}

	async updateById(model: Course, projectId: string) {
		const json = model.toJson();
		await this.db.getProyIdRef(projectId).collection(Course.PATH).doc(model.id).set(json, { merge: true });
	}

	async delete(course: Course, projectId: string) {
		course.isDeleted = true;
		const json = course.toJson();
		Promise.all([
			this.db.getProyIdRef(projectId).collection(Course.PATH).doc(course.id).set(json, { merge: true }),
			this.removeNestedVideosAssignments(json?.id, projectId)
		]);
	}

	async getAll(projectId: string) {
		const snapshot = await this.db.getProyIdRef(projectId).collection(Course.PATH).get();
		if (snapshot.empty) {
			return null;
		}
		const courses = snapshot.docs.map(course => new Course(course.data()));
		return courses;
	}

	/**
	 * The category's live courses, in category order. Deleting a course leaves it in every
	 * category's `courses`, so missing and deleted ones are dropped here; otherwise they would be
	 * loaded back and written into the category (or a duplicate of it) again.
	 */
	async getByCategory(category: Category, projectId: string) {
		const courseRefs: DocumentReference[] = category.courses ?? [];
		const snapshots = await Promise.all(courseRefs.map(courseRef => courseRef.get()));
		return snapshots
			.filter(snapshot => snapshot.exists && snapshot.data()?.['is_deleted'] !== true)
			.map(snapshot => new Course(snapshot.data()));
	}

	async getById(courseId: string, projectId: string) {
		const snapshot = await this.db.getProyIdRef(projectId).collection(Course.PATH).doc(courseId).get();
		const course = new Course(snapshot.data());
		return course;
	}

	async getByFilter(filter: string, projectId: string) {
		const snapshot = await this.db.getProyIdRef(projectId).collection(Course.PATH).orderBy('name').startAt(filter).endAt(filter + '\uf8ff').get();
		let courses: Course[] = [];
		snapshot.docs.forEach(item => {
			let data = item.data();
			let course = new Course(data);
			courses.push(course);
		});
		return courses;
	}

	async duplicate(model: Course, projectId: string, authUserRole: RoleEnum) {
		const newModel = cloneDeep(model);
		newModel.id = null;

		newModel.createdAt = firebase.firestore.Timestamp.now();
		newModel.status = PublishStatusEnum.DRAFT;
		newModel.assigned = false;
		newModel.name = `${model.name} - Copy`;
		newModel.nameLowercase = newModel.name.toLowerCase();
		newModel.id = await this.uniqueIdService.getOrCreateNextNumericId(Course.COUNTERCOLLECTION, Course.COUNTER, Course.COUNTERSTART);

		newModel.createdByAdmin = authUserRole === RoleEnum.ADMIN;

		const languageCourses = await this.languageCourseService.getById(model.id, projectId);
		newModel.duplicatedFrom = model.id;
		newModel.translationsInherited = (languageCourses ?? []).some(lc => !lc.isDefault && !lc.isDeleted);
		languageCourses.forEach(langCourse => (langCourse.id = null));
		let videos: string[] = [];
		languageCourses.forEach(langCourse => videos = videos.concat(langCourse?.chapters.map(chapter => chapter?.videoId).filter(Boolean)));
		await Promise.all([
			this.save(newModel, languageCourses, projectId),
			this.linkDuplicatedCoursesWithVideos(videos, newModel?.id, projectId)
		]);
	}

	async publish(model: Course, projectId: string) {
		await this.db.getProyIdRef(projectId).collection(Course.PATH).doc(model.id).update({
			status: PublishStatusEnum.PUBLISHED, ready_to_publish: false
		});
	}


	getRef(id: string, projectId: string): DocumentReference {
		return this.db.getProyIdRef(projectId).collection(Course.PATH).doc(id);
	}

	private async removeNestedVideosAssignments(courseId: string, projectId: string): Promise<void> {
		const languageCourses = await this.languageCourseService.getById(courseId, projectId);

		if (!languageCourses) {
			return;
		}

		let videos: string[] = [];
		for (const languageCourse of languageCourses) {
			videos = videos.concat(languageCourse?.chapters.map(item => item?.videoId));
		}
		const promises = videos.map(videoId => this.mediaLibraryService.resolveVideoAssignments(videoId, courseId, 'remove', projectId));
		await Promise.all(promises);
	}

	private async linkDuplicatedCoursesWithVideos(videoIds: string[], courseId: string, projectId: string): Promise<void> {
		const promises = videoIds.map(id => this.mediaLibraryService.resolveVideoAssignments(id, courseId, 'add', projectId));
		await Promise.all(promises);
	}

	private calculateDuration(chapters: Chapter[]): number {
		let duration = 0;
		chapters.forEach(chapter => {
			duration += chapter.duration;
		});
		return duration;
	}

	async translate(courseId: string, language: Language, videoUrls: string[], options: { subtitles: boolean, text: boolean }): Promise<string> {
		let promises: any[] = [];
		if (options?.subtitles) {
			promises = videoUrls.map(url => this.mediaLibraryService.generateVideoSubtitles(url, [language?.code]))
		}
		if (options?.text) {
			const data = {
				course_id: courseId,
				language: language,
				videos_url: videoUrls
			};
			const [translatedCourse] = await Promise.all([
				this.api.post('/courseLanguages/translate', data) as any,
				...promises
			]);
			return translatedCourse.id;
		}
		return null;
	}

	async regenerate(courseId: string, language: Language, videoUrls: string[], options: { subtitles: boolean, text: boolean }): Promise<string> {
		let promises: any[] = [];
		if (options?.subtitles) {
			promises = videoUrls.map(url => this.mediaLibraryService.generateVideoSubtitles(url, [language?.code]))
		}
		if (options?.text) {
			const data = {
				course_id: courseId,
				language: language,
				videos_url: videoUrls
			};
			const [regeneratedCourse] = await Promise.all([
				this.api.post('/courseLanguages/regenerate', data) as any,
				...promises
			]);
			return regeneratedCourse.id;
		}
		await Promise.all(promises);
		return null;
	}

	trackTranslationProcess(
		courseId: string,
		translationId: string,
		next: (snapshot: firebase.firestore.DocumentSnapshot<firebase.firestore.DocumentData>) => void,
		error: (error: firebase.firestore.FirestoreError) => void
	): () => void {
		return this.db.getProyIdRef(environment.projectId)
			.collection(Course.PATH)
			.doc(courseId)
			.collection(LanguageCourse.PATH)
			.doc(translationId)
			.onSnapshot(next, error);
	}

}
